'use client';

import { type FC, useCallback, useContext } from 'react';

import { VisuallyHidden } from '../visually-hidden';
import { PaginationRootContext } from './context/pagination-root.context';
import { usePaginationRange } from './hooks/use-pagination-range';
import { PaginationItem } from './pagination-item';
import { PaginationPageButton } from './pagination-page-button';
import type { PaginationPageItemsPropsInterface } from './types/pagination-items.types';
import type { PaginationPageButtonPropsInterface } from './types/pagination-page-button.types';

/**
 * Generates and renders numbered `PageButton`s
 * @since 10.0.0
 */
export const PaginationPageItems: FC<PaginationPageItemsPropsInterface> = (props) => {
  const {
    renderItem = ({ key, buttonProps }) => (
      <PaginationItem key={key}>
        <PaginationPageButton {...buttonProps} />
      </PaginationItem>
    ),
  } = props;

  const { currentPageIndex = 0, totalPages, setPageIndex } = useContext(PaginationRootContext);

  const render = useCallback(
    (pageIndex: number) => {
      const selected = currentPageIndex === pageIndex;
      const key = selected ? `selectedPageButton` : `page-${pageIndex}`;

      const buttonProps: PaginationPageButtonPropsInterface = {
        'selected': selected,
        'aria-current': selected ? 'page' : undefined,
        'children': (
          <>
            <VisuallyHidden>Page</VisuallyHidden> {pageIndex + 1}
          </>
        ),
        'onClick': () => setPageIndex?.(pageIndex),
      };

      return renderItem({ buttonProps, key, pageIndex });
    },
    [currentPageIndex, renderItem, setPageIndex],
  );

  const content = usePaginationRange({
    currentPageIndex,
    totalPages,
    maxNumOfPageButtons: 7,
    render,
  });

  return <>{content}</>;
};

PaginationPageItems.displayName = 'Pagination.PageItems';
